import React from "react";
import { ChevronDown, ChevronRight, Archive } from "lucide-react";
import { gbp, ukDate } from "../../lib/format";

export const UNCATEGORISED_KEY = "__uncategorised__";

/* Group materials by category KEY, in the Settings order (then A–Z for any
 * category not in the list), "(Uncategorised)" last. Rows inside a group are
 * name A–Z. Pure — the page decides what is open. */
export function groupMaterials(items, categories) {
  const order = new Map(categories.map((c, i) => [c.key, [c.order ?? i, c.label]]));
  const groups = new Map();
  for (const m of items) {
    const key = (m.category || "").trim() || UNCATEGORISED_KEY;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(m);
  }
  const out = Array.from(groups.entries()).map(([key, rows]) => {
    const label = key === UNCATEGORISED_KEY ? "(Uncategorised)" : (order.get(key)?.[1] || key);
    rows.sort((a, b) => (a.name || "").localeCompare(b.name || "", "en-GB", { sensitivity: "base" }));
    const live = rows.filter((m) => !m.is_archived).length;
    const updated = rows.reduce((mx, m) => (m.updated_at && m.updated_at > mx ? m.updated_at : mx), "");
    return { key, label, rows, live, archived: rows.length - live, updated };
  });
  out.sort((a, b) => {
    if (a.key === UNCATEGORISED_KEY) return 1;
    if (b.key === UNCATEGORISED_KEY) return -1;
    const oa = order.get(a.key), ob = order.get(b.key);
    if (oa && ob) return oa[0] - ob[0] || a.label.localeCompare(b.label);
    if (oa) return -1;
    if (ob) return 1;
    return a.label.localeCompare(b.label);
  });
  return out;
}

export const NO_SUPPLIER_KEY = "__no_supplier__";

/* Group by PREFERRED supplier (the column's rule: the preferred link, else the
 * first link), then by category inside each supplier — so "Travis Perkins"
 * collapses every Travis Perkins material and category into one row.
 * Suppliers A–Z, "(No supplier)" last. Child keys are `sup:<id>/<category>`
 * so open-state never collides with the category-only view. */
export function groupBySupplier(items, categories, supplierById) {
  const bySup = new Map();
  for (const m of items) {
    const links = m.supplier_links || [];
    const pref = links.find((l) => l.is_preferred) || links[0];
    const sid = pref?.supplier_id || NO_SUPPLIER_KEY;
    if (!bySup.has(sid)) bySup.set(sid, []);
    bySup.get(sid).push(m);
  }
  const out = Array.from(bySup.entries()).map(([sid, rows]) => {
    const sup = sid === NO_SUPPLIER_KEY ? null : supplierById[sid];
    const label = sid === NO_SUPPLIER_KEY ? "(No supplier)" : (sup?.name || "Unknown supplier");
    const key = `sup:${sid}`;
    const children = groupMaterials(rows, categories).map((g) => ({ ...g, key: `${key}/${g.key}` }));
    const live = rows.filter((m) => !m.is_archived).length;
    const updated = rows.reduce((mx, m) => (m.updated_at && m.updated_at > mx ? m.updated_at : mx), "");
    return { key, sid, label, archivedSupplier: sup?.status === "archived", rows, children, live, archived: rows.length - live, updated,
             categoryCount: children.length };
  });
  out.sort((a, b) => {
    if (a.sid === NO_SUPPLIER_KEY) return 1;
    if (b.sid === NO_SUPPLIER_KEY) return -1;
    return a.label.localeCompare(b.label, "en-GB", { sensitivity: "base" });
  });
  return out;
}

export function GroupHeader({ group, open, onToggle, depth = 0 }) {
  const Icon = open ? ChevronDown : ChevronRight;
  const top = depth === 0;
  return (
    <tr className={(top ? "bg-surface-2/50 hover:bg-surface-2" : "bg-surface-2/20 hover:bg-surface-2/50") + " border-b border-line cursor-pointer select-none"}
        onClick={onToggle} data-testid={`materials-group-${group.key}`} aria-expanded={open}>
      <td colSpan={9} className="px-3 py-2" style={{ paddingLeft: 12 + depth * 22 }}>
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-ink-3 shrink-0" />
          <span className={top ? "text-ink font-medium" : "text-ink-2"} style={{ fontSize: top ? 13 : 12.5 }} data-testid={`materials-group-${group.key}-label`}>
            {group.label}
            {group.archivedSupplier && <span className="ml-1.5 text-ink-4 italic" style={{ fontSize: 10.5 }}>(archived supplier)</span>}
          </span>
          {group.categoryCount != null && (
            <span className="text-ink-4" style={{ fontSize: 11.5 }} data-testid={`materials-group-${group.key}-categories`}>
              {group.categoryCount} {group.categoryCount === 1 ? "category" : "categories"} ·
            </span>
          )}
          <span className="font-mono tnum text-ink-3" style={{ fontSize: 11.5 }} data-testid={`materials-group-${group.key}-count`}>
            {group.live > 0
              ? <>{group.live} {group.live === 1 ? "material" : "materials"}{group.archived > 0 && <span className="text-ink-4"> · +{group.archived} archived</span>}</>
              : <span className="text-ink-4">{group.archived} archived</span>}
          </span>
          {group.updated && (
            <span className="ml-auto font-mono tnum text-ink-4" style={{ fontSize: 11 }}>updated {ukDate(group.updated)}</span>
          )}
        </div>
      </td>
    </tr>
  );
}

export function MaterialRow({ m, categoryLabel, unitLabel, supplierById, onOpen, last }) {
  const preferredLink = (m.supplier_links || []).find((l) => l.is_preferred) || (m.supplier_links || [])[0];
  const preferredSupplier = preferredLink ? supplierById[preferredLink.supplier_id] : null;
  return (
    <tr onClick={onOpen} data-testid={`material-row-${m.id}`}
        className={["group cursor-pointer hover:bg-surface-2/60", last ? "" : "border-b border-line-soft", m.is_archived ? "opacity-60" : ""].join(" ")}>
      <td className="px-4 py-3"><span className="font-mono tnum text-ink-2" style={{ fontSize: 12 }}>{m.code}</span></td>
      <td className="px-4 py-3"><span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>{m.name}</span></td>
      <td className="px-4 py-3">
        {m.category ? <span className="text-ink-2" style={{ fontSize: 12.5 }}>{categoryLabel}</span>
          : <span className="text-ink-4 italic" style={{ fontSize: 11.5 }}>Uncategorised</span>}
      </td>
      <td className="px-4 py-3"><span className="text-ink-3 font-mono" style={{ fontSize: 11.5 }}>{unitLabel}</span></td>
      <td className="px-4 py-3 text-right"><span className="font-mono tnum text-ink" style={{ fontSize: 13 }}>{gbp(m.current_price)}</span></td>
      <td className="px-4 py-3">
        {preferredSupplier ? (
          <span className="text-ink-2" style={{ fontSize: 12.5 }}>
            {preferredSupplier.name}
            {preferredSupplier.status === "archived" && <span className="ml-1.5 text-ink-4 italic" style={{ fontSize: 10.5 }}>(archived)</span>}
          </span>
        ) : <span className="text-ink-4" style={{ fontSize: 12 }}>—</span>}
        {preferredLink?.supplier_ref && <span className="font-mono text-ink-4 ml-2" style={{ fontSize: 11 }}>{preferredLink.supplier_ref}</span>}
      </td>
      <td className="px-4 py-3"><span className="font-mono tnum text-ink-3" style={{ fontSize: 12 }}>{ukDate(m.updated_at)}</span></td>
      <td className="px-4 py-3">
        {m.is_archived
          ? <span className="inline-flex items-center gap-1 text-ink-3" style={{ fontSize: 11 }}><Archive size={11} /> Archived</span>
          : <span className="text-accent" style={{ fontSize: 11 }}>Active</span>}
      </td>
      <td className="pr-4 py-3 text-right"><ChevronRight size={14} className="text-ink-4 group-hover:text-ink-2" /></td>
    </tr>
  );
}
